import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FaComments, FaEnvelope, FaClock, FaUser, FaBox, FaKey, 
  FaFileAlt, FaSpinner, FaArrowRight 
} from 'react-icons/fa';

export default function AllConversations() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [rentalConversations, setRentalConversations] = useState([]);
  const [requestConversations, setRequestConversations] = useState([]);
  const [quoteConversations, setQuoteConversations] = useState([]);
  const [productConversations, setProductConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchAllConversations();
  }, [user]);

  const fetchAllConversations = async () => {
    setLoading(true);
    try {
      // Fetch rental conversations
      const rentalRes = await axios.get(`${API}/rentals/conversations/list?user_id=${user.id}`);
      setRentalConversations(rentalRes.data || []);
      
      // Fetch request conversations
      const requestRes = await axios.get(`${API}/requests/conversations/my?user_id=${user.id}`);
      setRequestConversations(requestRes.data || []);
      
      // Fetch quote conversations (supplier quotes)
      try {
        const quoteRes = await axios.get(`${API}/quotes/my-threads?user_id=${user.id}`);
        setQuoteConversations(quoteRes.data || []);
      } catch {
        setQuoteConversations([]);
      }
      
      // Fetch product conversations (contact seller)
      try {
        const productRes = await axios.get(`${API}/products/conversations/my?user_id=${user.id}`);
        setProductConversations(productRes.data || []);
      } catch {
        setProductConversations([]);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const allConversations = [
    ...rentalConversations.map(c => ({ ...c, type: 'rental' })),
    ...requestConversations.map(c => ({ ...c, type: 'request' })),
    ...quoteConversations.map(c => ({ ...c, type: 'quote' })),
    ...productConversations.map(c => ({ ...c, type: 'product' }))
  ].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  const getConversationsByType = (type) => {
    if (type === 'all') return allConversations;
    if (type === 'product') return allConversations.filter(c => c.type === 'product' || c.type === 'quote');
    return allConversations.filter(c => c.type === type);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'rental': return <FaKey className="text-[#00D4FF]" />;
      case 'request': return <FaFileAlt className="text-[#635BFF]" />;
      case 'quote': return <FaBox className="text-[#0A2540]" />;
      case 'product': return <FaEnvelope className="text-green-500" />;
      default: return <FaComments className="text-gray-500" />;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'rental': 
        return <Badge className="bg-[#00D4FF] text-white">Location</Badge>;
      case 'request': 
        return <Badge className="bg-[#635BFF] text-white">Demande</Badge>;
      case 'quote': 
        return <Badge className="bg-[#0A2540] text-white">Devis Produit</Badge>;
      case 'product':
        return <Badge className="bg-green-500 text-white">Renseignement</Badge>;
      default: 
        return null;
    }
  };

  const handleConversationClick = (conv) => {
    switch (conv.type) {
      case 'rental':
        navigate(`/rentals/conversations/${conv.id}`);
        break;
      case 'request':
        // Navigate to request conversation page
        navigate(`/requests/conversations/${conv.id}`);
        break;
      case 'quote':
        // For quotes, use the supplier quote thread
        navigate(`/supplier/quotes/${conv.id}`);
        break;
      case 'product':
        // For product conversations, use the dedicated conversation route
        navigate(`/product/${conv.product_id}/conversation/${conv.id}`);
        break;
      default:
        break;
    }
  };

  const getConversationTitle = (conv) => {
    switch (conv.type) {
      case 'rental':
        return conv.product_name || 'Conversation Location';
      case 'request':
        return conv.request_title || 'Conversation Demande';
      case 'quote':
        return conv.product_name || 'Devis Produit';
      case 'product':
        return conv.product_name || 'Renseignement Produit';
      default:
        return 'Conversation';
    }
  };

  const getOtherParty = (conv) => {
    switch (conv.type) {
      case 'rental':
        const isOwner = user.id === conv.owner_id;
        return isOwner ? conv.renter_name : conv.owner_name;
      case 'request':
        const isRequester = user.id === conv.requester_id;
        return isRequester ? conv.supplier_name : conv.requester_name;
      case 'quote':
        return conv.vendor_name || conv.client_name;
      case 'product':
        const isBuyer = user.id === conv.buyer_id;
        return isBuyer ? conv.seller_name : conv.buyer_name;
      default:
        return 'Utilisateur';
    }
  };

  const getLastMessage = (conv) => {
    // Handle request conversations
    if (conv.type === 'request' && conv.messages && conv.messages.length > 0) {
      const lastMsg = conv.messages[conv.messages.length - 1];
      return lastMsg.content?.substring(0, 100) + (lastMsg.content?.length > 100 ? '...' : '');
    }
    
    // Handle product conversations (contact seller)
    if (conv.type === 'product' && conv.messages && conv.messages.length > 0) {
      const lastMsg = conv.messages[conv.messages.length - 1];
      return lastMsg.content?.substring(0, 100) + (lastMsg.content?.length > 100 ? '...' : '');
    }
    
    // Handle quotes where last_message is an object
    if (conv.last_message) {
      // Check if it's an object with content property
      if (typeof conv.last_message === 'object' && conv.last_message.content) {
        const content = conv.last_message.content;
        return content.substring(0, 100) + (content.length > 100 ? '...' : '');
      }
      // If it's a string
      if (typeof conv.last_message === 'string') {
        return conv.last_message.substring(0, 100) + (conv.last_message.length > 100 ? '...' : '');
      }
    }
    
    return i18n.language === 'fr' ? 'Aucun message' : 'No messages';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-2">
            {i18n.language === 'fr' ? 'Mes Conversations' : 'My Conversations'}
          </h1>
          <p className="text-gray-600">
            {i18n.language === 'fr' 
              ? 'Toutes vos conversations : produits, locations et demandes'
              : 'All your conversations: products, rentals and requests'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('all')}>
            <CardContent className="p-4 flex items-center gap-3">
              <FaComments className="text-2xl text-gray-500" />
              <div>
                <p className="text-2xl font-bold">{allConversations.length}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('product')}>
            <CardContent className="p-4 flex items-center gap-3">
              <FaBox className="text-2xl text-[#0A2540]" />
              <div>
                <p className="text-2xl font-bold">{quoteConversations.length + productConversations.length}</p>
                <p className="text-sm text-gray-500">Produits</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('rental')}>
            <CardContent className="p-4 flex items-center gap-3">
              <FaKey className="text-2xl text-[#00D4FF]" />
              <div>
                <p className="text-2xl font-bold">{rentalConversations.length}</p>
                <p className="text-sm text-gray-500">Locations</p>
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('request')}>
            <CardContent className="p-4 flex items-center gap-3">
              <FaFileAlt className="text-2xl text-[#635BFF]" />
              <div>
                <p className="text-2xl font-bold">{requestConversations.length}</p>
                <p className="text-sm text-gray-500">Demandes</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="all">Toutes</TabsTrigger>
            <TabsTrigger value="product">Produits</TabsTrigger>
            <TabsTrigger value="rental">Locations</TabsTrigger>
            <TabsTrigger value="request">Demandes</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {getConversationsByType(activeTab).length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FaEnvelope className="mx-auto text-6xl text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">
                    {i18n.language === 'fr' 
                      ? 'Aucune conversation pour le moment'
                      : 'No conversations yet'}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    {i18n.language === 'fr'
                      ? 'Les conversations apparaîtront ici lorsque vous interagirez avec d\'autres utilisateurs'
                      : 'Conversations will appear here when you interact with other users'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {getConversationsByType(activeTab).map((conv, index) => (
                  <Card
                    key={`${conv.type}-${conv.id}-${index}`}
                    className="hover:shadow-lg transition-all cursor-pointer"
                    onClick={() => handleConversationClick(conv)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getTypeIcon(conv.type)}
                            <h3 className="text-lg font-bold text-[#0A2540]">
                              {getConversationTitle(conv)}
                            </h3>
                            {getTypeBadge(conv.type)}
                          </div>

                          <div className="flex items-center gap-2 text-gray-600 mb-2">
                            <FaUser className="text-sm" />
                            <span className="text-sm">
                              Avec: <strong>{getOtherParty(conv)}</strong>
                            </span>
                          </div>

                          <p className="text-gray-500 text-sm mb-2 line-clamp-2">
                            {getLastMessage(conv)}
                          </p>

                          <div className="flex items-center gap-2 text-gray-400 text-xs">
                            <FaClock />
                            <span>{formatDate(conv.updated_at || conv.created_at)}</span>
                          </div>
                        </div>

                        <Button variant="ghost" size="sm" className="ml-4">
                          <FaArrowRight />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
